<app-modal [open]="open"
           [modalClass]="'modal--panel-right'"
           (closed)="close()"
>
  <div modal-header>
    @if (!service) {
      <h2>{{ 'services.create-modal.title' | transloco }}</h2>
    }
    @if (service) {
      <h2>{{ 'services.create-modal.edit.title' | transloco }}</h2>
    }
  </div>

  <div modal-body>
    <form [formGroup]="form" (ngSubmit)="create()">
      <section class="service-modal-section" [class.is-collapsed]="!sectionOpen.info">
        <button type="button" class="section-header" (click)="toggleSection('info')">
          <div class="section-header__titles">
            <h3>{{'services.create-modal.information.title'|transloco}}</h3>
            <p class="section-header__subtitle">{{ 'services.create-modal.information.subtitle' | transloco }}</p>
          </div>
          <i class="fa-solid fa-chevron-down" [class.rotated]="sectionOpen.info"></i>
        </button>

        @if (sectionOpen.info) {
          <div class="section-body">
            <div class="input-group">
              <label>{{ 'services.create-modal.name.label' | transloco }}</label>
              <input type="text"
                     formControlName="name"
                     placeholder="{{ 'services.create-modal.name.placeholder' | transloco }}" />
            </div>

            <div class="input-group">
              <label>{{ 'services.create-modal.description.label' | transloco }}</label>
              <textarea formControlName="description"
                        placeholder="{{ 'services.create-modal.description.placeholder' | transloco }}"></textarea>
            </div>

            <div class="input-group">
              <label>{{ 'services.create-modal.duration.label' | transloco }}</label>
              <input type="number"
                     formControlName="duration"
                     placeholder="{{ 'services.create-modal.duration.placeholder' | transloco }}" />
            </div>

            <div class="input-group input-group--toggle">
              <label>{{ 'services.create-modal.online.label' | transloco }}</label>
              <app-toggle [toggled]="form.value.online" (onChange)="onToggleOnline($event)"></app-toggle>
            </div>

            <app-helper-banner>
              <div helper-content>
                <p>{{ 'services.create-modal.online.help' | transloco }}</p>
              </div>
            </app-helper-banner>
          </div>
        }
      </section>

      <section class="service-modal-section" [class.is-collapsed]="!sectionOpen.photos">
        <button type="button" class="section-header" (click)="toggleSection('photos')">
          <div class="section-header__titles">
            <h3>{{'services.create-modal.photos.title'|transloco}}</h3>
            <p class="section-header__subtitle">{{ 'services.create-modal.photos.subtitle' | transloco }}</p>
          </div>
          <i class="fa-solid fa-chevron-down" [class.rotated]="sectionOpen.photos"></i>
        </button>

        @if (sectionOpen.photos) {
          <div class="section-body">
            <div class="input-group input-group--image">
              <div class="input-group__header">
                <label>{{ 'services.create-modal.image.main.label' | transloco }}</label>
                <span class="helper-text" [title]="'services.create-modal.image.main.helper' | transloco">
                  {{ 'services.create-modal.image.main.helper' | transloco }}
                </span>
              </div>
              <app-image-upload (imagesSelected)="onImageUploaded($event)"></app-image-upload>
              @if (mainImagePreviewUrl || service?.image) {
                <div class="service-gallery service-gallery--single">
                  <div class="service-gallery__item">
                    <app-image
                      class="service-gallery__thumb"
                      [src]="mainImagePreviewUrl ?? service?.image ?? ''"
                      [alt]="service?.name ?? 'Main service image'"
                      preset="card"
                      aspectRatio="4/3"
                      [width]="160"
                      [height]="120"
                    ></app-image>
                  </div>
                </div>
              }
            </div>

            <div class="input-group input-group--image">
              <div class="input-group__header">
                <label>{{ 'services.create-modal.photos.additional.label' | transloco }}</label>
                <span class="helper-text" [title]="'services.create-modal.photos.additional.helper' | transloco">
                  {{ 'services.create-modal.photos.additional.helper' | transloco:{ max: maxGalleryPhotos } }}
                </span>
              </div>
              <app-image-upload
                [maxNumberOfImages]="maxGalleryPhotos"
                [label]="'services.create-modal.photos.add' | transloco"
                (batchReady)="onGalleryBatchReady($event)"
              ></app-image-upload>

              @if (!service && queuedPhotos.length) {
                <div class="service-gallery">
                  @for (photo of queuedPhotos; track photo.previewUrl) {
                    <div class="service-gallery__item">
                      <app-image
                        class="service-gallery__thumb"
                        [src]="photo.previewUrl"
                        [alt]="photo.file.name"
                        preset="card"
                        aspectRatio="4/3"
                        [width]="120"
                        [height]="90"
                      ></app-image>
                      <button class="button button--small button--danger" type="button" (click)="removeQueuedPhoto(photo)">
                        <i class="fa-solid fa-trash-can"></i>
                        {{ 'services.create-modal.photos.remove' | transloco }}
                      </button>
                    </div>
                  }
                </div>
              }

              @if (service && galleryPhotos.length) {
                <div
                  class="service-gallery"
                  cdkDropList
                  (cdkDropListDropped)="dropPhoto($event)"
                >
                  @for (photo of galleryPhotos; track photo.id) {
                    <div class="service-gallery__item" cdkDrag>
                      <app-image
                        class="service-gallery__thumb"
                        [src]="photo.url"
                        [alt]="service.name"
                        preset="card"
                        aspectRatio="4/3"
                        [width]="120"
                        [height]="90"
                      ></app-image>
                      <div class="service-gallery__meta">
                        <span class="drag-handle" cdkDragHandle><i class="fa-solid fa-up-down-left-right"></i></span>
                        <button class="button button--small button--danger" type="button" (click)="deleteGalleryPhoto(photo)">
                          <i class="fa-solid fa-trash-can"></i>
                          {{ 'services.create-modal.photos.delete' | transloco }}
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </section>

      <section class="service-modal-section" [class.is-collapsed]="!sectionOpen.pricing">
        <button type="button" class="section-header" (click)="toggleSection('pricing')">
          <div class="section-header__titles">
            <h3>{{'services.create-modal.pricing.title'|transloco}}</h3>
            <p class="section-header__subtitle">{{ 'services.create-modal.pricing.subtitle' | transloco }}</p>
          </div>
          <i class="fa-solid fa-chevron-down" [class.rotated]="sectionOpen.pricing"></i>
        </button>

        @if (sectionOpen.pricing) {
          <div class="section-body">
            <div class="input-group"
                 [class.input-group--disabled]="form.value.priceRangeActive"
            >
              <label>{{ 'services.create-modal.price.label' | transloco }}</label>
              <input type="number"
                     step="0.01"
                     min="0"
                     formControlName="price"
                     placeholder="{{ 'services.create-modal.price.placeholder' | transloco }}" />
            </div>

            <div class="input-group input-group--toggle">
              <label>{{ 'services.create-modal.price-range.label' | transloco }}</label>
              <app-toggle [toggled]="form.value.priceRangeActive" (onChange)="onTogglePriceRange($event)"></app-toggle>
            </div>

            <app-helper-banner>
              <div helper-content>
                <p>{{ 'services.create-modal.price-range.help' | transloco }}</p>
              </div>
            </app-helper-banner>

            <div class="input-group--side-by-side input-group--price-range">
              <div class="input-group input-group--price"
                   [class.input-group--disabled]="!form.value.priceRangeActive"
              >
                <label>{{ 'services.create-modal.price-range.min.label' | transloco }}</label>
                <input type="number"
                       step="0.01"
                       min="0"
                       formControlName="priceRangeMin"
                       placeholder="{{ 'services.create-modal.price-range.min.placeholder' | transloco }}" />
              </div>

              <div class="input-group"
                   [class.input-group--disabled]="!form.value.priceRangeActive"
              >
                <label>{{ 'services.create-modal.price-range.max.label' | transloco }}</label>
                <input type="number"
                       step="0.01"
                       min="0"
                       formControlName="priceRangeMax"
                       placeholder="{{ 'services.create-modal.price-range.max.placeholder' | transloco }}" />
              </div>
            </div>

            <div class="input-group input-group--toggle">
              <label>{{ 'services.create-modal.discountActive.label' | transloco }}</label>
              <app-toggle [toggled]="form.value.discountActive" (onChange)="onToggleDiscountActive($event)"></app-toggle>
            </div>

            <div class="input-group"
                 [class.input-group--disabled]="!form.value.discountActive"
            >
              <label>{{ 'services.create-modal.discount.label' | transloco }}</label>
              <input type="number"
                     step="1"
                     min="0"
                     max="100"
                     formControlName="discount"
                     placeholder="{{ 'services.create-modal.discount.placeholder' | transloco }}" />
            </div>

            <div class="input-group input-group--toggle">
              <label>{{ 'services.create-modal.deposit.allow.label' | transloco }}</label>
              <app-toggle [toggled]="form.value.allowDeposit" (onChange)="onToggleDeposit($event)"></app-toggle>
            </div>

            <app-helper-banner>
              <div helper-content>
                <p>{{ 'services.create-modal.deposit.help' | transloco }}</p>
              </div>
            </app-helper-banner>

            <div class="input-group input-group--deposit"
                 [class.input-group--disabled]="!form.value.allowDeposit"
            >
              <label>{{ 'services.create-modal.deposit.percentage.label' | transloco }}</label>
              <div class="input-group__with-suffix">
                <input type="number"
                       step="1"
                       min="1"
                       max="99"
                       formControlName="depositPercentage"
                       placeholder="{{ 'services.create-modal.deposit.percentage.placeholder' | transloco }}" />
                <span class="input-group__suffix">%</span>
              </div>
            </div>
          </div>
        }
      </section>

      @if (service) {
        <section class="service-modal-section" [class.is-collapsed]="!sectionOpen.danger">
          <button type="button" class="section-header" (click)="toggleSection('danger')">
            <div class="section-header__titles">
              <h3>{{'services.create-modal.danger-zone.title'|transloco}}</h3>
              <p class="section-header__subtitle">{{'services.create-modal.danger-zone.subtitle'|transloco}}</p>
            </div>
            <i class="fa-solid fa-chevron-down" [class.rotated]="sectionOpen.danger"></i>
          </button>
          @if (sectionOpen.danger) {
            <div class="section-body">
              <div class="danger-zone">
                <div class="danger-zone__header">
                  <div class="danger-zone__header-title">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <h3>{{'services.create-modal.danger-zone.title'|transloco}}</h3>
                  </div>
                  <p class="danger-zone__header-subtitle">{{'services.create-modal.danger-zone.subtitle'|transloco}}</p>
                </div>
                <div class="danger-zone__content">
                  <button class="button button--danger"
                          type="button"
                          (click)="delete()"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                    {{'services.create-modal.danger-zone.delete'|transloco}}
                  </button>
                </div>
              </div>
            </div>
          }
        </section>
      }
    </form>
  </div>

  <div modal-footer>
    <div class="actions">
      <button type="button" class="button button--secondary" (click)="close()">
        {{ 'services.create-modal.cancel' | transloco }}
      </button>
      @if (!service) {
        <button type="submit"
                class="button"
                [disabled]="form.invalid || editing"
                [class.button--loading]="editing"
                (click)="create()"
        >
          {{ 'services.create-modal.create' | transloco }}
        </button>
      }
      @if (service) {
        <button type="submit"
                class="button"
                [disabled]="form.invalid || editing"
                [class.button--loading]="editing"
                (click)="edit()"
        >
          {{ 'services.create-modal.save' | transloco }}
        </button>
      }
    </div>
  </div>
</app-modal>
